import type { QueryKey } from '@tanstack/react-query'
import {
  adminKeys,
  attendanceKeys,
  calendarKeys,
  catalogKeys,
  categoryTemplateKeys,
  changeRequestKeys,
  courseDetailKeys,
  dashboardKeys,
  gradeLevelKeys,
  gradePostingKeys,
  gradeScaleKeys,
  gradebookKeys,
  homeroomKeys,
  myGradeChangeKeys,
  parentKeys,
  postedGradeKeys,
  settingsKeys,
  studentAttendanceKeys,
  studentCustomFieldKeys,
  studentDetailKeys,
  studentGradesKeys,
  studentRecordKeys,
  submissionKeys,
  teacherSubmissionKeys,
  yearEndGradeKeys,
} from './queryKeys'

/**
 * What a write makes stale, everywhere — not just on the page that made it.
 *
 * The defect this exists for: a record created or edited on one screen went on
 * reading as its old self on every other screen until a reload. The cause is
 * that the same server resource is cached under several roots, because the roots
 * grew per *page*. One term-course's enrollments are three separate cache
 * entries — `adminKeys.enrollments()` behind the admin table,
 * `courseDetailKeys.enrollments(tc)` behind the course page's picker,
 * `gradebookKeys.enrollments(tc)` behind the marking grid — and a fourth,
 * `studentDetailKeys.detail(studentId)`, on the child's own record. Enrolling a
 * student touched whichever one its own page happened to read, so the other
 * three kept answering from a cache that predated the enrollment for the length
 * of the app's one-minute `staleTime`.
 *
 * Every mutation now declares the *resource* it wrote rather than the keys it
 * happens to know about, and this module answers with the whole set. That is the
 * point of the indirection: a new screen that caches enrollments under a new key
 * is added to `enrollments()` once, here, and every write that touches an
 * enrollment picks it up — instead of being one more entry that a dozen call
 * sites each have to remember.
 *
 * ## Scope arguments
 *
 * Each function takes what the *caller* knows. Passing a `termCourseId` narrows
 * the fan-out to that course's entries; omitting it widens to the prefix, which
 * is correct but refetches more. Never invent one: an id guessed to make the set
 * smaller invalidates the wrong course and leaves the right one stale.
 *
 * ## Cost
 *
 * Invalidation is cheap where nothing is watching. `invalidateQueries` refetches
 * only the queries that currently have observers; everything else is marked
 * stale and re-read if and when it is next mounted. A student edit that names
 * `gradebookKeys.rosters()` therefore costs a refetch only for someone who is
 * *looking at* a gradebook, which is exactly the case the fan-out is for. These
 * sets are awaited (see `useOptimisticMutation`), so they should still say what
 * a write genuinely touches and not everything adjacent to it.
 */

interface TermCourseScope {
  termCourseId?: string
}

interface StudentScope {
  studentId?: string
}

/**
 * The marking grid's own caches for one course — or, with no course named, every
 * cached grid at once.
 *
 * The roster and the enrollments are always a pair: the enrollments carry the
 * server's weighted term grade that the grid's headline column prints, and the
 * roster's cache key is derived from the enrolled ids (see `gradebookRosterQuery`).
 */
function gradebookScope({ termCourseId }: TermCourseScope): QueryKey[] {
  return termCourseId
    ? [gradebookKeys.enrollments(termCourseId), gradebookKeys.students(termCourseId)]
    : [gradebookKeys.all]
}

export const cacheSync = {
  /**
   * A person on the roster: created, edited, withdrawn, reinstated, imported.
   *
   * The directory and the per-role lists are the same rows through narrower
   * windows. `homeroomKeys` is here because a group's headcount is counted
   * server-side over roster presence, and the gradebook rosters because the grid
   * prints the child's name. Attendance reports print the name and the child's
   * current homeroom, and leave out a withdrawn child who has no marks.
   */
  students({ studentId }: StudentScope = {}): QueryKey[] {
    return [
      adminKeys.students(),
      adminKeys.peopleAll(),
      homeroomKeys.all,
      attendanceKeys.reports(),
      dashboardKeys.stats(),
      gradebookKeys.rosters(),
      ...(studentId
        ? [studentDetailKeys.detail(studentId), studentDetailKeys.grades(studentId)]
        : [studentDetailKeys.all]),
    ]
  },

  /**
   * Who is taking a course. The one resource with the widest fan-out, and the
   * one the original defect was reported against.
   *
   * `courseDetailKeys.all` rather than the two scoped entries: the course page's
   * own header is keyed by *course* id, which an enrollment write never carries,
   * and its enrollment counts are exactly what this changes.
   */
  enrollments({ termCourseId, studentId }: TermCourseScope & StudentScope = {}): QueryKey[] {
    return [
      adminKeys.enrollments(),
      adminKeys.students(),
      courseDetailKeys.all,
      dashboardKeys.stats(),
      ...gradebookScope({ termCourseId }),
      ...(studentId ? [studentDetailKeys.detail(studentId)] : [studentDetailKeys.all]),
    ]
  },

  /** A course in the catalogue. Its name and code are printed on every section. */
  courses(): QueryKey[] {
    return [catalogKeys.courses(), dashboardKeys.stats(), ...cacheSync.termCourses()]
  },

  /**
   * A section: one course, one term, one teacher.
   *
   * The admin table, the gradebook's scope and the Settings dropdown all list
   * these, and all three used to be separate cache entries because three pages
   * each grew their own root. They are one entry now (`catalogKeys`), which is
   * why this set names one key where it used to name three.
   */
  termCourses(): QueryKey[] {
    return [
      catalogKeys.termCourses(),
      courseDetailKeys.all,
      adminKeys.enrollments(),
      gradePostingKeys.all,
      dashboardKeys.stats(),
    ]
  },

  /**
   * A term: its dates, its name, and its status — which is what decides whether
   * a gradebook may still be marked, so the grid's own caches are in scope.
   */
  terms(): QueryKey[] {
    return [
      adminKeys.terms(),
      catalogKeys.termCourses(),
      courseDetailKeys.all,
      gradePostingKeys.all,
      submissionKeys.all,
      teacherSubmissionKeys.all,
      gradebookKeys.submissions(),
      // Locking a term is what posts its grades, and unlocking removes them
      // again — so the posted grades and the year-end view built on them move
      // with the term's status, not independently of it.
      postedGradeKeys.all,
      yearEndGradeKeys.all,
    ]
  },

  /** An academic year. Terms hang off it, and homerooms are set up per year. */
  academicYears(): QueryKey[] {
    return [adminKeys.academicYears(), adminKeys.terms(), homeroomKeys.all]
  },

  /**
   * A staff record. Their name is denormalised onto sections and homerooms, and
   * attendance reports print it beside each homeroom.
   */
  teachers(): QueryKey[] {
    return [
      adminKeys.teachers(),
      adminKeys.peopleAll(),
      catalogKeys.termCourses(),
      courseDetailKeys.all,
      homeroomKeys.all,
      attendanceKeys.reports(),
      dashboardKeys.stats(),
    ]
  },

  /** A parent record. */
  parents(): QueryKey[] {
    return [adminKeys.parents(), adminKeys.peopleAll(), adminKeys.parentStudentLinks()]
  },

  /** A parent-student link: who may be contacted about, and see, which child. */
  parentLinks({ studentId }: StudentScope = {}): QueryKey[] {
    return [
      adminKeys.parentStudentLinks(),
      parentKeys.all,
      ...(studentId ? [studentDetailKeys.detail(studentId)] : [studentDetailKeys.all]),
    ]
  },

  /**
   * Anyone in the directory, whatever offices they hold.
   *
   * A person write can land in any of the per-role lists — creating one with the
   * teacher role puts them on the staff list, granting `parent` puts them on the
   * parents list — and which one is not knowable from the call site, so all of
   * them are named.
   */
  people(): QueryKey[] {
    return [
      adminKeys.peopleAll(),
      adminKeys.teachers(),
      adminKeys.parents(),
      adminKeys.students(),
      dashboardKeys.stats(),
    ]
  },

  /** A department. Teachers and courses both file under one. */
  departments(): QueryKey[] {
    return [
      adminKeys.departments(),
      adminKeys.teachers(),
      catalogKeys.courses(),
      courseDetailKeys.all,
    ]
  },

  /**
   * A pastoral group. The register is taken per homeroom, so moving children
   * between groups changes who is on tomorrow's roll call.
   */
  homerooms(): QueryKey[] {
    return [
      homeroomKeys.all,
      adminKeys.students(),
      attendanceKeys.all,
      dashboardKeys.stats(),
    ]
  },

  /**
   * A course's assignment categories and their weights.
   *
   * Cached twice for one endpoint — `settingsKeys.categories(tc)` for the
   * settings screen that edits them, `gradebookKeys.categories(tc)` for the grid
   * that marks against them — and the weights decide the term grade, so the
   * roster is stale too.
   */
  categories({ termCourseId }: { termCourseId: string }): QueryKey[] {
    return [
      settingsKeys.categories(termCourseId),
      gradebookKeys.categories(termCourseId),
      gradebookKeys.assignments(termCourseId),
      ...gradebookScope({ termCourseId }),
    ]
  },

  /**
   * The templates a new course's categories are stamped out of.
   *
   * A template's weight flows down into every course category locked to it, so
   * the per-course category lists are stale as well — both copies of them. The
   * call sites used to spell the settings one as the literal `['settings',
   * 'categories']` and never named the gradebook's at all.
   */
  categoryTemplates(): QueryKey[] {
    return [
      categoryTemplateKeys.all,
      settingsKeys.allCategories(),
      gradebookKeys.allCategories(),
      gradebookKeys.rosters(),
    ]
  },

  /**
   * A piece of work. `calendarKeys` because "upcoming assignments" is the
   * dashboard's read of exactly this, and it is the one that looked most broken:
   * a teacher set a deadline and the calendar went on not knowing about it.
   */
  assignments({ termCourseId }: { termCourseId: string }): QueryKey[] {
    return [
      gradebookKeys.assignments(termCourseId),
      calendarKeys.all,
      studentDetailKeys.courseAssignments(termCourseId),
      studentGradesKeys.all,
      parentKeys.all,
      ...gradebookScope({ termCourseId }),
    ]
  },

  /**
   * A mark. The weighted term grade the server derives from it rides on the
   * enrollment, and the child's own record and their parent's portal both report
   * it.
   */
  grades({ termCourseId, studentId }: TermCourseScope & StudentScope = {}): QueryKey[] {
    return [
      ...gradebookScope({ termCourseId }),
      studentGradesKeys.all,
      parentKeys.all,
      yearEndGradeKeys.all,
      ...(studentId
        ? [studentDetailKeys.detail(studentId), studentDetailKeys.grades(studentId)]
        : [studentDetailKeys.all]),
    ]
  },

  /**
   * A term's grades being handed in, approved or sent back.
   *
   * Four caches watch this one row: the admin queue, the teacher's own list, the
   * coverage table on Grade Posting, and the banner at the top of the gradebook
   * itself. The decision writes are addressed by submission id and never learn
   * the term-course, so the banner is reached by prefix.
   */
  submissions({ termCourseId }: TermCourseScope = {}): QueryKey[] {
    return [
      submissionKeys.all,
      teacherSubmissionKeys.all,
      gradePostingKeys.all,
      dashboardKeys.stats(),
      termCourseId ? gradebookKeys.submission(termCourseId) : gradebookKeys.submissions(),
    ]
  },

  /**
   * A request to change a posted grade, and its decision.
   *
   * The teacher's list and the admin queue are two reads of the same rows; an
   * approval also rewrites the posted grade itself, which is the transcript's.
   */
  gradeChangeRequests({ termCourseId }: TermCourseScope = {}): QueryKey[] {
    return [
      changeRequestKeys.all,
      myGradeChangeKeys.all,
      termCourseId ? postedGradeKeys.byTermCourse(termCourseId) : postedGradeKeys.all,
      yearEndGradeKeys.all,
      dashboardKeys.stats(),
    ]
  },

  /**
   * A mark on the register, from either end of it — the teacher's roll call or
   * the front desk. Both land in one attendance record, so the child's own
   * attendance tab is a reader of every such write.
   */
  attendance(): QueryKey[] {
    return [attendanceKeys.all, studentAttendanceKeys.all, dashboardKeys.stats()]
  },

  /**
   * The same, minus the registers themselves.
   *
   * For the register's own save, which answers with the whole register and
   * writes it straight into the cache — refetching it would be a second round
   * trip for an answer just received, and in between the counts would be one
   * save out of date (see `useSaveAttendance`). Everything *else* that was
   * showing that day still has to be told.
   */
  attendanceExceptRegisters(): QueryKey[] {
    return [
      attendanceKeys.overviews(),
      attendanceKeys.reports(),
      studentAttendanceKeys.all,
      dashboardKeys.stats(),
    ]
  },

  /** One child's authorized pickups. */
  authorizedPickups({ studentId }: { studentId: string }): QueryKey[] {
    return [studentAttendanceKeys.pickups(studentId)]
  },

  /** A student's health record or uploaded documents. */
  studentRecord({ studentId }: { studentId: string }): QueryKey[] {
    return [
      studentRecordKeys.health(studentId),
      studentRecordKeys.documents(studentId),
      studentDetailKeys.detail(studentId),
    ]
  },

  /** A batch of forms waiting to be read into student records. */
  intakeBatches({ batchId }: { batchId?: string } = {}): QueryKey[] {
    return [
      studentRecordKeys.intakeBatches(),
      ...(batchId ? [studentRecordKeys.intakeBatch(batchId)] : []),
    ]
  },

  /** The school's grade levels. Their labels are printed across the app. */
  gradeLevels(): QueryKey[] {
    return [
      gradeLevelKeys.all,
      adminKeys.students(),
      catalogKeys.courses(),
      homeroomKeys.all,
    ]
  },

  /**
   * A grade scale band. Every displayed letter and IB grade is derived through
   * one, so moving a threshold restates marks that are already on screen.
   *
   * Not `settingsKeys.all`: the only thing cached under `['settings']` is a
   * course's assignment categories, which a moved threshold does not touch —
   * and Settings is where the bands are edited, so naming that prefix refetched
   * the category list on the very screen doing the editing, every time.
   */
  gradeScales(): QueryKey[] {
    return [
      gradeScaleKeys.all,
      gradebookKeys.rosters(),
      studentDetailKeys.all,
      studentGradesKeys.all,
      parentKeys.all,
      yearEndGradeKeys.all,
    ]
  },

  /** A custom field's *definition* — what the student form offers. */
  studentCustomFields(): QueryKey[] {
    return [studentCustomFieldKeys.all, adminKeys.students(), studentDetailKeys.all]
  },
}
